import { useSignal } from '@preact/signals';

import type { EventRoleKind } from '@amumin/api-client';
import { t } from '@amumin/i18n';
import { Checkbox, Notice } from '@amumin/ui';
import { Avatar, cx, Icon, memberStyle } from '@amumin/ui-core';

import { joinNames, type MemberLite } from '../lib/agenda.js';
import {
  collapseDays,
  droppedRideDays,
  formWeekdays,
  startDays,
  type RideDays,
  type RidesFormPart,
  type RoleChoice,
} from '../lib/rides.js';
import { appText, messages } from '../state/session.js';
import styles from './RidesEditor.module.css';

// Brengen en halen in de afspraak-editor (0022, DESIGN.md §8.W3): rustige keuzes met gezichtjes
// in plaats van keuzelijsten. Eenvoudig: één keuze voor brengen en één voor halen. Bij een
// herhaling op twee of meer weekdagen kan "Per dag anders": per dag (ma, do ...) wie brengt en wie
// haalt; op brede schermen als tabel, op de telefoon één kaart per dag. Reistijd en het seintje om
// te vertrekken blijven in de editor zelf.

const ROLES: readonly EventRoleKind[] = ['dropoff', 'pickup'];

/** Wie mag rijden: de volwassenen (zonder volwassenen iedereen, zoals voorheen). */
export function driversOf(members: ReadonlyArray<MemberLite>): MemberLite[] {
  const adults = members.filter((x) => x.kind !== 'child');
  return adults.length > 0 ? adults : [...members];
}

/** Rond gezichtje van een keuze: lid, "nog niet bekend" (?) of "niet nodig" (−). */
export function ChoiceFace({
  choice,
  members,
  px = 32,
}: {
  choice: RoleChoice;
  members: ReadonlyArray<MemberLite>;
  px?: number;
}) {
  const member = members.find((x) => x.id === choice);
  if (member) {
    return (
      <Avatar
        name={member.display_name}
        colorKey={member.color_key}
        icon={member.avatar_icon}
        size="chip"
        px={px}
        decorative
      />
    );
  }
  return (
    <span
      class={cx(styles.face, choice === 'none' ? styles.faceNone : styles.faceOpen)}
      style={`width:${px}px;height:${px}px`}
      aria-hidden="true"
    >
      <Icon id={choice === 'none' ? 'minus' : 'circle-help'} size={Math.round(px * 0.6)} />
    </span>
  );
}

/** Naam van een keuze in gewone taal. */
export function choiceLabel(choice: RoleChoice, members: ReadonlyArray<MemberLite>): string {
  const a = appText.value;
  if (choice === 'none') return a.ridesPerDay.notNeeded;
  if (choice === 'open') return a.ridesPerDay.unknown;
  return members.find((x) => x.id === choice)?.display_name ?? a.ridesPerDay.unknown;
}

/**
 * Keuzerondjes als grote chips met gezichtje en naam (zelfde vorm als de ledenkiezer). Echte
 * radioknoppen: toetsenbord (pijltjes) en schermlezer werken.
 */
export function DriverChoices({
  name,
  legend,
  hideLegend = false,
  value,
  onChange,
  drivers,
  allowNone = true,
  allowOpen = true,
  dataName,
}: {
  name: string;
  legend: string;
  hideLegend?: boolean;
  value: RoleChoice | null;
  onChange: (choice: RoleChoice) => void;
  drivers: ReadonlyArray<MemberLite>;
  allowNone?: boolean;
  allowOpen?: boolean;
  dataName?: string;
}) {
  const a = appText.value;
  const options: RoleChoice[] = [
    ...drivers.map((x) => x.id),
    ...(allowOpen ? ['open'] : []),
    ...(allowNone ? ['none'] : []),
  ];
  return (
    <fieldset class={cx('member-picker', styles.choices)} data-name={dataName}>
      <legend class={hideLegend ? 'fb-sr-only' : 'field-label'}>{legend}</legend>
      <div class="member-picker-options">
        {options.map((choice) => {
          const member = drivers.find((x) => x.id === choice);
          const checked = value === choice;
          return (
            <label
              key={choice}
              class={cx('member-chip', checked && 'is-checked', !member && styles.quietChip)}
              data-checked={checked ? 'true' : 'false'}
              data-choice={member ? member.id : choice}
              style={member ? memberStyle(member.color_key) : undefined}
            >
              <input type="radio" name={name} checked={checked} onChange={() => onChange(choice)} />
              <ChoiceFace choice={choice} members={drivers} />
              <span>
                {member
                  ? member.display_name
                  : choice === 'open'
                    ? a.ridesPerDay.unknown
                    : a.ridesPerDay.notNeeded}
              </span>
              {checked ? <Icon id="check" size={16} class="member-chip-check" /> : null}
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}

export interface RidesChange {
  dropoff?: RoleChoice;
  pickup?: RoleChoice;
  rideDays?: RideDays | null;
}

export function RidesEditor({
  form,
  initial,
  members,
  onChange,
}: {
  form: RidesFormPart;
  /** Het formulier zoals het geopend werd (bewerken); null bij een nieuwe afspraak. */
  initial: RidesFormPart | null;
  members: ReadonlyArray<MemberLite>;
  onChange: (change: RidesChange) => void;
}) {
  const a = appText.value;
  const m = messages.value;
  const r = a.ridesPerDay;
  const openCell = useSignal<string | null>(null);
  const drivers = driversOf(members);
  const weekdays = formWeekdays(form);
  const perDayPossible = weekdays.length >= 2;
  const perDay = perDayPossible && form.rideDays !== null;
  const dropped = droppedRideDays(initial, weekdays);
  const roleName = (role: EventRoleKind) =>
    role === 'dropoff' ? a.editor.dropoff : a.editor.pickup;
  const dayName = (wd: number) => m.weekdays[wd - 1] ?? '';

  const setDay = (wd: number, role: EventRoleKind, choice: RoleChoice) => {
    const days = startDays(form, weekdays);
    const current = days[wd] ?? { dropoff: form.dropoff, pickup: form.pickup };
    days[wd] = { ...current, [role]: choice };
    openCell.value = null;
    onChange({ rideDays: days });
  };

  const togglePerDay = (on: boolean) => {
    openCell.value = null;
    if (on) onChange({ rideDays: startDays(form, weekdays) });
    else onChange({ ...collapseDays(form, weekdays), rideDays: null });
  };

  return (
    <div class={styles.editor} data-name="rides-editor">
      {!perDay ? (
        <div class={styles.simple}>
          {ROLES.map((role) => (
            <DriverChoices
              key={role}
              name={`ev-${role}`}
              legend={roleName(role)}
              value={form[role]}
              onChange={(choice) =>
                onChange(role === 'dropoff' ? { dropoff: choice } : { pickup: choice })
              }
              drivers={drivers}
              dataName={`ride-${role}`}
            />
          ))}
        </div>
      ) : (
        <div class={styles.table} data-name="ride-days">
          <div class={styles.headRow} aria-hidden="true">
            <span>{r.day}</span>
            <span>{a.editor.dropoff}</span>
            <span>{a.editor.pickup}</span>
          </div>
          {weekdays.map((wd) => {
            const day = form.rideDays?.[wd] ?? { dropoff: form.dropoff, pickup: form.pickup };
            const openRole = ROLES.find((role) => openCell.value === `${wd}-${role}`);
            return (
              <div key={wd} class={styles.day} data-name={`ride-day-${wd}`}>
                <span class={styles.dayName}>
                  <span class={styles.dayShort} aria-hidden="true">
                    {m.weekdaysShort[wd - 1]}
                  </span>
                  <span class={styles.dayLong}>{dayName(wd)}</span>
                </span>
                {ROLES.map((role) => {
                  const choice = day[role];
                  const member = drivers.find((x) => x.id === choice);
                  const key = `${wd}-${role}`;
                  const isOpen = openCell.value === key;
                  return (
                    <div key={role} class={styles.cell}>
                      <span class={styles.cellLabel} aria-hidden="true">
                        {roleName(role)}
                      </span>
                      <button
                        type="button"
                        class={cx(
                          styles.cellButton,
                          member && styles.cellMember,
                          choice === 'none' && styles.cellNone,
                        )}
                        style={member ? memberStyle(member.color_key) : undefined}
                        aria-expanded={isOpen ? 'true' : 'false'}
                        aria-controls={`ride-panel-${key}`}
                        aria-label={`${t(r.roleOnDay, { role: roleName(role), day: dayName(wd) })}: ${choiceLabel(choice, drivers)}. ${r.change}`}
                        data-name={`ride-cell-${wd}-${role}`}
                        onClick={() => (openCell.value = isOpen ? null : key)}
                      >
                        <ChoiceFace choice={choice} members={drivers} px={28} />
                        <span class={styles.cellText}>{choiceLabel(choice, drivers)}</span>
                        <Icon id={isOpen ? 'chevron-up' : 'chevron-down'} size={16} />
                      </button>
                    </div>
                  );
                })}
                {openRole ? (
                  <div id={`ride-panel-${wd}-${openRole}`} class={styles.panel}>
                    <DriverChoices
                      name={`ev-${openRole}-${wd}`}
                      legend={t(r.roleOnDay, { role: roleName(openRole), day: dayName(wd) })}
                      value={day[openRole]}
                      onChange={(choice) => setDay(wd, openRole, choice)}
                      drivers={drivers}
                      dataName={`ride-choices-${wd}-${openRole}`}
                    />
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
      {perDayPossible ? (
        <Checkbox
          id="ev-rides-per-day"
          label={r.perDay}
          checked={perDay}
          onChange={togglePerDay}
          hint={
            perDay
              ? undefined
              : drivers.length >= 2
                ? t(r.perDayHint, {
                    a: drivers[0]?.display_name ?? '',
                    b: drivers[1]?.display_name ?? '',
                  })
                : r.perDayHintOne
          }
        />
      ) : null}
      {dropped.length > 0 ? (
        <Notice kind="info" name="rides-dropped">
          {t(r.droppedDays, {
            count: dropped.length,
            // Voluit ("donderdag"): "do" leest in een zin dubbelzinnig (review c-reg r1).
            days: joinNames(
              dropped.map((d) => (m.weekdays[d - 1] ?? '').toLowerCase()),
              a.reminders.and,
            ),
          })}
        </Notice>
      ) : null}
    </div>
  );
}
